/**
 * Starter content at Create Tenant — the decisions, as pure functions
 * (CRM Foundation decision 9, UX contract artboard 13).
 *
 * Kept out of `CreateTenantPage.jsx` so they can be tested: that page imports
 * `react-router-dom`, which this repo's Jest cannot resolve.
 *
 *   - DEVELOPMENT, DEMO and STAGING start ON.
 *   - LIVE has NO default: the operator must choose, and the page cannot be
 *     submitted until they do. The server's own default for an unstated LIVE
 *     request is OFF; the screen asks rather than relying on it.
 *   - Only licensed products are offered, each saying what it adds.
 *   - The six starter reports and the Sales Overview dashboard are always
 *     added — configuration, not sample data.
 */

/** The products with starter content, in display order — the Platform's keys. */
export const STARTER_MODULES = Object.freeze([
  {
    key: 'crm',
    label: 'CRM',
    adds: '4 accounts, 5 contacts, 4 prospects, 4 opportunities, 13 tasks and 7 events, plus '
      + 'one prospect converted into an account, contact and opportunity. These feed '
      + 'Follow-Ups, Tasks Due and the starter dashboard.',
  },
  {
    key: 'engage',
    label: 'Engage',
    adds: '1 form, 1 survey and 1 landing page. All saved as drafts. Nothing is published.',
  },
  {
    key: 'task_manager',
    label: 'Task Manager',
    adds: '6 tasks: 2 overdue, 1 due today and 3 upcoming.',
  },
  {
    key: 'booking',
    label: 'Booking',
    adds: '1 personal booking type and 1 meeting type. Both stay off until a calendar is connected.',
  },
]);

export const ALWAYS_ADDED_TITLE = 'Always added: 6 starter reports and the Sales Overview dashboard.';
export const ALWAYS_ADDED_BODY = 'They are configuration, not sample data. They run on this '
  + "tenant's own records and show empty states until there are some.";

export const SAFETY_LINES = Object.freeze([
  'Every sample record is named "(Sample)"',
  'Nothing is emailed, called, texted or published',
  'Running setup again never adds a second copy',
  'Delete a sample record like any other record',
]);

const DEFAULT_ON = new Set(['DEVELOPMENT', 'DEMO', 'STAGING']);

/**
 * The starting value for an environment: `true` where starter content is on
 * by default, `null` for LIVE — "not chosen yet", which blocks submission.
 */
export function defaultStarterContent(environment) {
  const code = String(environment || '').toUpperCase();
  if (DEFAULT_ON.has(code)) return true;
  return null;
}

/** Live (and anything unknown) has no default: the operator must choose. */
export function requiresExplicitChoice(environment) {
  return defaultStarterContent(environment) === null;
}

/**
 * Which products this tenant is licensed for, from the commercial
 * configuration the operator is building. CRM Console is included with every
 * tenant. The server filters again against the entitlements it resolves, so
 * this list only decides what is OFFERED.
 */
export function licensedStarterModules(commercial) {
  const codes = new Set((commercial?.lines || []).map((l) => l && l.item_code).filter(Boolean));
  return STARTER_MODULES
    .filter((m) => m.key === 'crm' || codes.has(m.key))
    .map((m) => m.key);
}

/**
 * The modules the request names: the operator's ticks, never wider than the
 * licence. `selected === null` means "untouched" — every licensed module.
 */
export function chosenStarterModules(selected, licensed) {
  if (selected == null) return [...licensed];
  return licensed.filter((key) => selected.includes(key));
}

/** Why the form cannot be submitted, for the starter-content step; or null. */
export function starterContentBlockingReason(form) {
  if (form.include_sample_data === null || form.include_sample_data === undefined) {
    return 'Choose whether to add sample content. Live tenants have no default.';
  }
  return null;
}

/** What the review step says. */
export function describeStarterContentChoice(form, licensed) {
  if (form.include_sample_data === null || form.include_sample_data === undefined) {
    return 'Not chosen yet';
  }
  if (!form.include_sample_data) return 'Not included';
  const chosen = chosenStarterModules(form.starter_content_modules, licensed);
  if (!chosen.length) return 'Included, no products selected';
  const labels = STARTER_MODULES.filter((m) => chosen.includes(m.key)).map((m) => m.label);
  return `Included: ${labels.join(', ')}`;
}
